import { useState, useRef, useCallback } from 'react';
import type { UploadCategory, FileMetadata } from '@seedgovernance/shared';
import { useWorkspace } from '../../hooks/useWorkspace';
import { apiClient } from '../../lib/api';
import { Button } from '../../components/ui/Button';

interface UploadUrlResponse {
  sasUrl: string;
  blobName: string;
}

interface FileUploaderProps {
  onUploaded: (file: FileMetadata) => void;
  category?: UploadCategory;
  linkedDeliverable?: string;
}

type UploadState = 'idle' | 'getting-url' | 'uploading' | 'confirming' | 'done' | 'error';

export function FileUploader({ onUploaded, category, linkedDeliverable }: FileUploaderProps) {
  const { currentWorkspace } = useWorkspace();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const handleFile = useCallback(
    async (file: File) => {
      if (!currentWorkspace) return;

      setSelectedFile(file);
      setError(null);
      setProgress(0);

      try {
        // Step 1: Get SAS upload URL
        setUploadState('getting-url');
        const { data: urlData, error: urlError } = await apiClient.post<UploadUrlResponse>(
          `/api/tenants/${currentWorkspace.tenantId}/files/upload-url`,
          {
            filename: file.name,
            mimeType: file.type,
            sizeBytes: file.size,
            category: category || 'general',
            linkedDeliverable: linkedDeliverable || null,
          }
        );

        if (urlError || !urlData) {
          throw new Error(urlError?.message || 'Failed to get upload URL');
        }

        // Step 2: Upload directly to Azure Blob Storage
        setUploadState('uploading');
        const uploadResponse = await fetch(urlData.sasUrl, {
          method: 'PUT',
          headers: {
            'x-ms-blob-type': 'BlockBlob',
            'Content-Type': file.type,
          },
          body: file,
        });

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload file to storage');
        }

        setProgress(100);

        // Step 3: Confirm upload
        setUploadState('confirming');
        const { data: confirmData, error: confirmError } = await apiClient.post<FileMetadata>(
          `/api/tenants/${currentWorkspace.tenantId}/files/confirm`,
          {
            blobName: urlData.blobName,
            filename: file.name,
            mimeType: file.type,
            sizeBytes: file.size,
            category: category || 'general',
            linkedDeliverable: linkedDeliverable || null,
          }
        );

        if (confirmError || !confirmData) {
          throw new Error(confirmError?.message || 'Failed to confirm upload');
        }

        setUploadState('done');
        onUploaded(confirmData);

        // Reset after a brief pause
        setTimeout(() => {
          setUploadState('idle');
          setSelectedFile(null);
          setProgress(0);
        }, 2000);
      } catch (err) {
        setUploadState('error');
        setError(err instanceof Error ? err.message : 'Upload failed');
      }
    },
    [currentWorkspace, category, linkedDeliverable, onUploaded]
  );

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);

    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(true);
  }

  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  const isUploading = uploadState !== 'idle' && uploadState !== 'done' && uploadState !== 'error';

  const statusLabel = () => {
    switch (uploadState) {
      case 'getting-url': return 'Preparing upload...';
      case 'uploading': return 'Uploading...';
      case 'confirming': return 'Confirming...';
      case 'done': return 'Upload complete!';
      case 'error': return 'Upload failed';
      default: return '';
    }
  };

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleInputChange}
      />

      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !isUploading && fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
          dragOver
            ? 'border-brand bg-brand/5'
            : isUploading
            ? 'border-border bg-surface-dark cursor-default'
            : 'border-border hover:border-slate-600 bg-surface-dark'
        }`}
      >
        {uploadState === 'idle' && (
          <>
            <svg className="w-10 h-10 text-slate-500 mx-auto mb-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17,8 12,3 7,8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <p className="text-sm text-slate-300 mb-1">
              Drop a file here or click to browse
            </p>
            <p className="text-xs text-slate-500">
              PDF, DOCX, XLSX, CSV, and image files supported
            </p>
          </>
        )}

        {(isUploading || uploadState === 'done' || uploadState === 'error') && selectedFile && (
          <div>
            <div className="flex items-center justify-center gap-2 mb-3">
              <svg className="w-5 h-5 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                <polyline points="13,2 13,9 20,9" />
              </svg>
              <span className="text-sm text-white truncate max-w-xs">
                {selectedFile.name}
              </span>
              <span className="text-xs text-slate-500">
                ({formatBytes(selectedFile.size)})
              </span>
            </div>

            <p className={`text-sm mb-2 ${
              uploadState === 'done' ? 'text-growth' :
              uploadState === 'error' ? 'text-red-400' :
              'text-slate-400'
            }`}>
              {statusLabel()}
            </p>

            {isUploading && (
              <div className="w-48 mx-auto h-1.5 bg-surface-mid rounded-full overflow-hidden">
                <div
                  className="h-full bg-brand rounded-full transition-all"
                  style={{ width: `${uploadState === 'uploading' ? 60 : uploadState === 'confirming' ? 90 : progress}%` }}
                />
              </div>
            )}

            {uploadState === 'done' && (
              <svg className="w-6 h-6 text-growth mx-auto" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20,6 9,17 4,12" />
              </svg>
            )}

            {error && (
              <div className="mt-2">
                <p className="text-xs text-red-400">{error}</p>
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    setUploadState('idle');
                    setSelectedFile(null);
                    setError(null);
                  }}
                >
                  Try Again
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
